type LocalizedField = string | Record<string, unknown> | null | undefined

export function getLocationKey(val: LocalizedField): string {
  if (val == null) return ""
  if (typeof val !== "object" || Array.isArray(val)) return String(val)
  const keys = Object.keys(val as object)
  return keys.length > 0 ? String(keys[0]) : ""
}

export function getLocationDisplay(val: LocalizedField): string {
  if (val == null) return ""
  if (typeof val !== "object" || Array.isArray(val)) return String(val)
  const keys = Object.keys(val as object)
  if (keys.length === 0) return ""
  const value = (val as Record<string, unknown>)[keys[0]]
  const keyStr = String(keys[0])
  if (typeof value === "string" && value.length > 0) return value
  return keyStr
}

export function toPlainStringForApi(val: LocalizedField): string {
  return getLocationDisplay(val) || getLocationKey(val)
}

type OrbitLocationRegion = {
  region_code?: string
  region_name?: unknown
  countries?: Array<{
    countryCode?: string
    alternateCountryCode?: string
    countryName?: unknown
    country_name_key?: string
  }>
}

export function normalizeLocationFilterValues(
  locations: string[] | undefined,
  orbitLocationsRoot: unknown
): string[] {
  if (!locations?.length) return locations ?? []
  if (locations.includes("all")) return [...locations]

  const root = orbitLocationsRoot as {
    data?: { locations?: OrbitLocationRegion[] }
  }
  const tree = root?.data?.locations
  if (!tree?.length) return [...locations]

  const canonical = new Set<string>()
  const variantToCanonical = new Map<string, string>()

  for (const region of tree) {
    const rKey = region.region_code || String(region.region_name ?? "")
    if (rKey) canonical.add(rKey)

    for (const c of region.countries ?? []) {
      const key = c.country_name_key || String(c.countryName ?? "")
      if (!key) continue
      canonical.add(key)
      if (c.countryName && c.countryName !== key) {
        variantToCanonical.set(String(c.countryName), key)
      }
      if (c.countryCode && c.countryCode !== key) {
        variantToCanonical.set(c.countryCode, key)
      }
    }
  }

  return locations.map((loc) => {
    if (loc === "all" || canonical.has(loc)) return loc
    return variantToCanonical.get(loc) ?? loc
  })
}

// Maps each ISO-3 country code (as stored on an account's hq_country) to its
// canonical region key and country key from the OrbitShift locations taxonomy.
// Lets us resolve an account HQ to the region/country values a location tree
// emits (region_code for a whole region, country_name_key for one country).
export function buildHqCountryIndex(
  orbitLocationsRoot: unknown
): Record<string, { region: string; countryKey: string }> {
  const root = orbitLocationsRoot as {
    data?: { locations?: OrbitLocationRegion[] }
  }
  const tree = root?.data?.locations
  const index: Record<string, { region: string; countryKey: string }> = {}
  if (!tree?.length) return index

  for (const region of tree) {
    const regionKey = region.region_code || String(region.region_name ?? "")
    for (const c of region.countries ?? []) {
      const countryKey = c.country_name_key || String(c.countryName ?? "")
      if (c.countryCode)
        index[c.countryCode] = { region: regionKey, countryKey }
    }
  }
  return index
}

// Resolves ANY location variant to the canonical region/country keys the
// location tree emits. Handles the account HQ format (ISO-3 `countryCode`),
// the people-location format (`all_locations_people`, which stores
// `country_name_key` / `countryName` / a region code), and region-level values.
// Keyed case-insensitively by every known alias so either source resolves.
// A region-level value resolves with an empty `countryKey`.
export function buildLocationKeyIndex(
  orbitLocationsRoot: unknown
): Record<string, { region: string; countryKey: string }> {
  const root = orbitLocationsRoot as {
    data?: { locations?: OrbitLocationRegion[] }
  }
  const tree = root?.data?.locations
  const index: Record<string, { region: string; countryKey: string }> = {}
  if (!tree?.length) return index
  const put = (
    alias: unknown,
    entry: { region: string; countryKey: string }
  ) => {
    const k = String(alias ?? "")
      .trim()
      .toLowerCase()
    if (k && !(k in index)) index[k] = entry
  }
  for (const region of tree) {
    const regionKey = region.region_code || String(region.region_name ?? "")
    const regionEntry = { region: regionKey, countryKey: "" }
    put(region.region_code, regionEntry)
    put(region.region_name, regionEntry)
    for (const c of region.countries ?? []) {
      const countryKey = c.country_name_key || String(c.countryName ?? "")
      const entry = { region: regionKey, countryKey }
      put(c.country_name_key, entry)
      put(c.countryName, entry)
      put(c.countryCode, entry)
    }
  }
  return index
}

export type LocationTreeNode = {
  key: string
  title: string
  value: string
  // code is the country's ISO 3166-1 alpha-2 code when the taxonomy has one.
  children: { key: string; title: string; value: string; code?: string }[]
}

// TreeSelect values -> country names: a region value becomes its child titles,
// a country value passes through. Order preserved, duplicates dropped.
export function expandLocationSelection(
  tree: LocationTreeNode[],
  picked: string[]
): string[] {
  const byRegion = new Map(tree.map((r) => [r.value, r]))
  const out: string[] = []
  const seen = new Set<string>()
  for (const v of picked) {
    const region = byRegion.get(v)
    const values = region ? region.children.map((c) => c.title) : [v]
    for (const x of values) {
      if (x && !seen.has(x)) {
        seen.add(x)
        out.push(x)
      }
    }
  }
  return out
}

// Inverse of expandLocationSelection: a region with every child present collapses
// to the region node, other countries map to their node, unknown names are dropped.
export function collapseLocationSelection(
  tree: LocationTreeNode[],
  names: string[]
): string[] {
  const wanted = new Set(names.filter(Boolean))
  if (!wanted.size) return []
  const out: string[] = []
  for (const region of tree) {
    const kids = region.children
    if (kids.length && kids.every((c) => wanted.has(c.title))) {
      out.push(region.value)
      for (const c of kids) wanted.delete(c.title)
      continue
    }
    for (const c of kids) {
      if (wanted.has(c.title)) {
        out.push(c.value)
        wanted.delete(c.title)
      }
    }
  }
  return out
}

// TreeSelect values -> ISO-2 country codes: a region value becomes its
// children's codes, a country value becomes that country's code. Order
// preserved; duplicates and nodes without a code dropped.
export function expandLocationSelectionToCodes(
  tree: LocationTreeNode[],
  picked: string[]
): string[] {
  const byRegion = new Map(tree.map((r) => [r.value, r]))
  const codeByValue = new Map<string, string>()
  for (const r of tree) {
    for (const c of r.children) if (c.code) codeByValue.set(c.value, c.code)
  }
  const out: string[] = []
  const seen = new Set<string>()
  for (const v of picked) {
    const region = byRegion.get(v)
    const codes = region
      ? region.children.map((c) => c.code)
      : [codeByValue.get(v)]
    for (const code of codes) {
      if (code && !seen.has(code)) {
        seen.add(code)
        out.push(code)
      }
    }
  }
  return out
}

// Inverse of expandLocationSelectionToCodes: a region whose every coded child
// is present collapses to the region node, other codes map to their country
// node, unknown codes are dropped.
export function collapseLocationSelectionFromCodes(
  tree: LocationTreeNode[],
  codes: string[]
): string[] {
  const wanted = new Set(
    codes.map((c) => c.trim().toUpperCase()).filter(Boolean)
  )
  if (!wanted.size) return []
  const out: string[] = []
  for (const region of tree) {
    const kids = region.children.filter((c) => c.code)
    if (kids.length && kids.every((c) => wanted.has(c.code ?? ""))) {
      out.push(region.value)
      for (const c of kids) wanted.delete(c.code ?? "")
      continue
    }
    for (const c of kids) {
      if (c.code && wanted.has(c.code)) {
        out.push(c.value)
        wanted.delete(c.code)
      }
    }
  }
  return out
}

// Builds AntD TreeSelect data (region → country) from the OrbitShift locations
// taxonomy. Shared by the exec-geography filter and the account-HQ region filter.
export function buildLocationTreeData(
  orbitLocationsRoot: unknown
): LocationTreeNode[] {
  const root = orbitLocationsRoot as {
    data?: { locations?: OrbitLocationRegion[] }
  }
  return (
    root?.data?.locations?.map((item) => {
      const regionName = item.region_name as LocalizedField
      const regionKey = item.region_code || getLocationKey(regionName)
      return {
        key: regionKey,
        title: getLocationDisplay(regionName) || String(item.region_name ?? ""),
        value: regionKey,
        children: (item.countries ?? []).map((c) => {
          const countryKey = c.country_name_key || String(c.countryName ?? "")
          return {
            key: countryKey,
            title: String(c.countryName ?? "") || countryKey,
            value: countryKey,
            code: c.alternateCountryCode || undefined
          }
        })
      }
    }) ?? []
  )
}
